import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Users,
  ToggleRight,
  ClipboardList,
  Activity,
  ChevronRight,
  UserPlus,
  Sparkles,
  MessageSquare,
  TrendingUp,
  Mail,
  Radio,
  DollarSign,
} from "lucide-react";
import { adminApi } from "@/admin/api";

const Tile = ({ to, icon: Icon, title, value, sub, tone = "green" }) => {
  const content = (
    <>
      <span className={`grid place-items-center w-10 h-10 rounded-lg bg-totem-${tone}/15 text-totem-${tone}`}>
        <Icon size={18} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="label-caps mb-1">{title}</div>
        <div className="display-heading text-2xl">{value}</div>
        {sub && <div className="mono text-[10px] uppercase text-totem-textDim mt-1">{sub}</div>}
      </div>
      {to && (
        <ChevronRight className="text-totem-textDim group-hover:text-totem-green transition" size={16} />
      )}
    </>
  );
  const cls = "totem-card p-5 flex items-start gap-4 transition group";
  return to ? (
    <Link to={to} className={`${cls} hover:border-totem-green/40`}>{content}</Link>
  ) : (
    <div className={cls}>{content}</div>
  );
};

const MiniStat = ({ label, value, sub, accent = "text-totem-text" }) => (
  <div className="totem-card-inner p-3">
    <div className="mono uppercase text-[9px] tracking-wider text-totem-textDim">{label}</div>
    <div className={`display-heading text-xl mt-1 ${accent}`}>{value}</div>
    {sub && <div className="mono text-[9px] uppercase text-totem-textFaint mt-0.5">{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const { data: health, isLoading } = useQuery({
    queryKey: ["admin-health"],
    queryFn: async () => (await adminApi.get("/health")).data,
    staleTime: 30_000,
  });
  const { data: metrics, isLoading: mLoading } = useQuery({
    queryKey: ["admin-metrics"],
    queryFn: async () => (await adminApi.get("/metrics")).data,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const mongo = health?.mongo || {};
  const probes = health?.probes || [];
  const u = metrics?.users || {};
  const e = metrics?.engagement || {};
  const c = metrics?.comms || {};
  const dash = mLoading || isLoading;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-dashboard">
      <div>
        <div className="mono uppercase text-[11px] tracking-widest text-totem-textDim">Dashboard</div>
        <h1 className="display-heading text-3xl">System overview</h1>
      </div>

      {/* Key North-Star metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="admin-metrics-tiles">
        <Tile to="/admin/users" icon={Users} title="Total users" value={dash ? "…" : (u.total ?? 0)} sub="linked FPL managers" />
        <Tile icon={TrendingUp} title="MAU" value={dash ? "…" : (u.mau ?? 0)} sub="active last 30 days" tone="gold" />
        <Tile icon={Radio} title="WAU" value={dash ? "…" : (u.wau ?? 0)} sub="active last 7 days" tone="green" />
        <Tile icon={Activity} title="DAU" value={dash ? "…" : (u.dau ?? 0)} sub="active last 24 hours" tone="red" />
      </div>

      {/* Growth & retention */}
      <section className="totem-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="label-caps">Growth &amp; retention</div>
            <div className="text-xs text-totem-textDim mt-0.5">Signal for external decks — signups + engagement ratios.</div>
          </div>
          {u.total > 0 && (
            <div className="mono text-[10px] uppercase text-totem-gold">
              MAU/Total {Math.round((u.mau / u.total) * 100)}%
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniStat label="New (7d)" value={dash ? "…" : (u.new_7d ?? 0)} accent="text-totem-green" />
          <MiniStat label="New (30d)" value={dash ? "…" : (u.new_30d ?? 0)} />
          <MiniStat label="Blocked" value={dash ? "…" : (u.blocked ?? 0)} accent="text-totem-red" />
          <MiniStat
            label="Stickiness"
            value={dash ? "…" : (u.mau ? `${Math.round((u.dau / u.mau) * 100)}%` : "—")}
            sub="DAU / MAU"
            accent="text-totem-gold"
          />
        </div>
      </section>

      {/* Ask FPL engagement */}
      <section className="totem-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="text-totem-gold" size={16} />
            <div>
              <div className="label-caps">Ask FPL Scout</div>
              <div className="text-xs text-totem-textDim mt-0.5">AI chat adoption and conversation depth.</div>
            </div>
          </div>
          <div className="mono text-[10px] uppercase text-totem-gold">
            Adoption {dash ? "…" : `${e.ask_adoption_pct ?? 0}%`}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniStat label="Total sessions" value={dash ? "…" : (e.ask_sessions_total ?? 0)} accent="text-totem-gold" />
          <MiniStat label="Sessions / user" value={dash ? "…" : (e.sessions_per_active_user ?? 0)} sub="active chatters" />
          <MiniStat label="Total messages" value={dash ? "…" : (e.ask_messages_total ?? 0)} sub={`${e.ask_user_messages_total ?? 0} from users`} />
          <MiniStat label="Sessions (7d)" value={dash ? "…" : (e.ask_sessions_7d ?? 0)} accent="text-totem-green" />
        </div>
      </section>

      {/* Comms / digest */}
      <section className="totem-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="text-totem-green" size={16} />
          <div>
            <div className="label-caps">Weekly Roast Digest</div>
            <div className="text-xs text-totem-textDim mt-0.5">Email deliverability and opt-in rate.</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniStat label="Opt-ins" value={dash ? "…" : (c.digest_opt_in_users ?? 0)} sub="with email" />
          <MiniStat label="Opt-in rate" value={dash ? "…" : `${c.digest_opt_in_rate_pct ?? 0}%`} accent="text-totem-green" />
          <MiniStat label="Runs" value={dash ? "…" : (c.digest_runs ?? 0)} sub="scheduled fires" />
          <MiniStat label="Emails sent" value={dash ? "…" : (c.digest_emails_sent ?? 0)} accent="text-totem-gold" />
        </div>
      </section>

      {/* LLM Spend */}
      <section className="totem-card p-5" data-testid="admin-llm-spend">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="text-totem-gold" size={16} />
            <div>
              <div className="label-caps">Ask FPL — LLM spend</div>
              <div className="text-xs text-totem-textDim mt-0.5">Emergent Universal Key credit usage (estimated).</div>
            </div>
          </div>
          {e.pro_users !== undefined && (
            <div className="mono text-[10px] uppercase text-totem-gold">
              Pro users: {e.pro_users}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniStat
            label="Spend (7d)"
            value={dash ? "…" : `$${(metrics?.llm?.last_7d?.cost_usd ?? 0).toFixed(3)}`}
            sub={`${(metrics?.llm?.last_7d?.input_tokens ?? 0).toLocaleString()} in · ${(metrics?.llm?.last_7d?.output_tokens ?? 0).toLocaleString()} out`}
            accent="text-totem-gold"
          />
          <MiniStat
            label="Spend (30d)"
            value={dash ? "…" : `$${(metrics?.llm?.last_30d?.cost_usd ?? 0).toFixed(3)}`}
            sub={`${(metrics?.llm?.last_30d?.assistant_messages ?? 0)} replies`}
          />
          <MiniStat
            label="All-time spend"
            value={dash ? "…" : `$${(metrics?.llm?.all_time?.cost_usd ?? 0).toFixed(3)}`}
            sub="since launch"
            accent="text-totem-green"
          />
          <MiniStat
            label="Cost / active user"
            value={dash ? "…" : `$${(metrics?.llm?.cost_per_active_user_30d ?? 0).toFixed(4)}`}
            sub="30d spend ÷ MAU chatters"
            accent="text-totem-gold"
          />
        </div>
        <div className="text-[10px] mono text-totem-textFaint mt-3">
          Estimates based on Claude Sonnet 4.6 pricing (${'$'}3/M input · ${'$'}15/M output). Balance top-ups in Emergent → Profile → Universal Key.
        </div>
      </section>

      {/* System tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Tile to="/admin/features" icon={ToggleRight} title="Feature flags" value={isLoading ? "…" : (mongo.feature_flags ?? 0)} sub="visible + premium toggles" />
        <Tile to="/admin/audit" icon={ClipboardList} title="Audit log" value={isLoading ? "…" : (mongo.admin_audit_log ?? 0)} sub="admin actions recorded" />
        <Tile to="/admin/health" icon={Activity} title="Health probes" value={probes.filter((p) => p.status === "success").length + "/" + probes.length} sub={probes.length ? (probes.every((p) => p.status === "success") ? "all systems ok" : "attention") : "checking…"} />
        <Tile to="/admin/users" icon={UserPlus} title="Signups (7d)" value={dash ? "…" : (u.new_7d ?? 0)} sub="new linked managers" tone="green" />
      </div>

      <section className="totem-card p-5">
        <div className="label-caps mb-3">Editorial content</div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Link to="/admin/set-pieces" className="totem-card-inner p-3 hover:border-totem-gold/40 transition">
            <div className="mono uppercase text-[10px] text-totem-textDim">Set piece takers</div>
            <div className="display-heading text-xl mt-1">{isLoading ? "…" : (mongo.set_piece_takers ?? 0)}</div>
          </Link>
          <Link to="/admin/lineups" className="totem-card-inner p-3 hover:border-totem-gold/40 transition">
            <div className="mono uppercase text-[10px] text-totem-textDim">Predicted lineups</div>
            <div className="display-heading text-xl mt-1">{isLoading ? "…" : (mongo.predicted_lineups ?? 0)}</div>
          </Link>
        </div>
      </section>

      <div className="text-[10px] mono text-totem-textFaint text-center">
        <MessageSquare size={10} className="inline mr-1 -mt-0.5" />
        Metrics refresh every 60s · generated {metrics?.generated_at ? new Date(metrics.generated_at).toLocaleTimeString() : "—"}
      </div>
    </div>
  );
}
